// client/src/coms/components/comslayout_parts/useServerReadSummary.js
// Per-server unread/mention badges for server navigation, from a single request.
// Replaces polling GET /text-channels/:id/read-state for every channel of every server, which
// cost one request per channel and also marked every channel as "being viewed" on the server,
// suppressing push notifications while the client was open.
import { useEffect, useState } from 'react';
import { comsApiGet } from '../../comsApi';

const VISIBLE_POLL_MS = 3000;
const HIDDEN_POLL_MS = 15000;

function toInt(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.floor(n) : fallback;
}

function mapsEqual(a, b) {
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => a[k] === b[k]);
}

export function parseReadSummary(res) {
  const input = res && res.ok && res.servers && typeof res.servers === 'object' ? res.servers : {};
  const unread = {};
  const mentions = {};
  for (const key of Object.keys(input)) {
    const sid = String(key || '').trim();
    if (!sid) continue;
    const row = input[key] && typeof input[key] === 'object' ? input[key] : {};
    unread[sid] = toInt(row.unread, 0) > 0 ? 1 : 0;
    mentions[sid] = Math.max(0, toInt(row.mention_count, 0));
  }
  return { unread, mentions };
}

export default function useServerReadSummary({ enabled = true } = {}) {
  const [serverUnreadById, setServerUnreadById] = useState({});
  const [serverMentionCountById, setServerMentionCountById] = useState({});

  useEffect(() => {
    if (!enabled) return undefined;

    let alive = true;
    let timer = null;
    let inFlight = false;

    function isHidden() {
      try {
        return document.visibilityState === 'hidden';
      } catch {
        return false;
      }
    }

    function schedule() {
      if (!alive) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(run, isHidden() ? HIDDEN_POLL_MS : VISIBLE_POLL_MS);
    }

    async function run() {
      if (!alive || inFlight) return;
      inFlight = true;
      try {
        const res = await comsApiGet('/servers/read-summary');
        if (!alive) return;
        const { unread, mentions } = parseReadSummary(res);
        setServerUnreadById((prev) => (mapsEqual(prev, unread) ? prev : unread));
        setServerMentionCountById((prev) => (mapsEqual(prev, mentions) ? prev : mentions));
      } catch {
        // Keep the last known badges on transient failures rather than flashing them away.
      } finally {
        inFlight = false;
        schedule();
      }
    }

    function onVisibility() {
      if (!isHidden()) run();
    }

    run();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled]);

  return { serverUnreadById, serverMentionCountById };
}
